import {
  asBoolean,
  asString,
  asStringList,
  isRecord,
} from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * The `ask_user` payload, normalized for rendering.
 *
 * The third interrupt type, read the way the other two are. Its payload used to be picked apart where
 * it was needed — the interrupt component, the rail's placeholder, and the store's `pick()` — and the
 * two readings of `options` had already drifted apart on what counts as one. A field the harness
 * adds belongs in one file, not three.
 */
export type QuestionDisplay = {
  question: string
  /** Answers to click. Frequently absent, and only useful when it holds strings. */
  options: string[]
  recommended: string | null
  storyId: string | null
  /** What the harness's own checks found wrong with the question and could not fix. A question is
   * rewritten up to twice before this, so showing it means the maker can still answer knowing the
   * agent's own reader would not have. */
  hardFailures: string[]
  /** Softer findings on the question, worth a quiet line rather than a callout. */
  warnings: string[]
}

export function toQuestionDisplay(payload: unknown): QuestionDisplay {
  const record = isRecord(payload) ? payload : {}

  return {
    question: asString(record['question']),
    options: asStringList(record['options']),
    recommended:
      typeof record['recommended'] === 'string'
        ? asString(record['recommended']).trim() || null
        : null,
    storyId:
      typeof record['story_id'] === 'string' ? asString(record['story_id']).trim() || null : null,
    hardFailures: asStringList(record['hard_failures']),
    warnings: asStringList(record['gate_warnings']),
  }
}

/**
 * A `specialist_review` payload, read for the confirm card.
 *
 * `effects` is the harness's own prose — it already words the commit and no-commit cases
 * differently — so it is shown as written rather than rebuilt from the flags beside it. The flags
 * are read anyway, because `commit` is the difference between a WIP branch and a certified write
 * and belongs on screen as a fact of its own.
 */
export type SpecialistDisplay = {
  /** e.g. `writer.application_descriptions.v1`. */
  capability: string
  /** What running this does, in the harness's words. */
  effects: string
  /** The branch the new version is created on. */
  branch: string
  /** The committed version the work starts from. */
  sourceAppVersionId: string
  createsVersion: boolean
  persists: boolean
  /** False on the default path: the write lands on a WIP branch and is not committed. */
  commits: boolean
}

export function toSpecialistDisplay(payload: unknown): SpecialistDisplay {
  const record = isRecord(payload) ? payload : {}

  return {
    capability: asString(record['capability']),
    effects: asString(record['effects']),
    branch: asString(record['branch']),
    sourceAppVersionId: asString(record['source_app_version_id']),
    createsVersion: asBoolean(record['create_version'], false),
    persists: asBoolean(record['persist'], false),
    commits: asBoolean(record['commit'], false),
  }
}
